import React from 'react';
import { MenuProps } from 'antd';
import {
  DashboardOutlined,
  CalendarOutlined,
  TeamOutlined,
  WalletOutlined,
  BarChartOutlined,
  SettingOutlined,
  LayoutOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  GiftOutlined,
  FileTextOutlined,
  TagsOutlined,
  AccountBookOutlined,
  TagOutlined,
  ThunderboltOutlined,
  UserOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useEventContext } from '../../contexts/EventContext';
import { TenantContext, UserProfile } from '../../types';
import { useTranslation } from '../../hooks/useTranslation';
import { useIsOrgAdmin } from '../../hooks/useIsOrgAdmin';
import { useIsOutletScoped } from '../../hooks/useIsOutletScoped';
import { isModuleEnabled, OrgModuleKey } from '../../utils/orgModules';
import { HIDDEN_IN_LOYALTY_FOCUS, isLoyaltyFocus } from '../../utils/menuFocus';
import { cmsMenuItems } from '@riptik/cms-backoffice-module';
import { fnbposMenuItems } from '@riptik/fnbpos-backoffice-module';
import { courtMenuItems } from '@riptik/court-backoffice-module';

// Helper type: a single menu item from Ant Design MenuProps
type MenuItem = NonNullable<MenuProps['items']>[number]

interface OrganizationMenuProps {
  tenant: TenantContext;
  user: UserProfile;
  selectedKeys: string[];
  openKeys: string[];
  onOpenChange: (openKeys: string[]) => void;
  onMenuClick: (path: string) => void;
}

export const OrganizationMenu = ({
  tenant,
  user,
  onMenuClick,
}: OrganizationMenuProps): { items: MenuItem[] } => {
  const { getOrgUrl } = useEventContext();
  const { t } = useTranslation();
  const isOrgAdminUser = useIsOrgAdmin();
  const isOutletManagerOnly = useIsOutletScoped(); // task 070-E: module F&B/POS chỉ cho quản trị tổ chức (bo-gateway chỉ map org_admin → admin)

  // Simple role-based access check
  const hasRoleAccess = (key: string): boolean => {
    if (!user) return false;

    // Debug logging
    // if (key === 'settings') {
    //   console.log('[OrganizationMenu] Settings access check:', {
    //     user_type: user.user_type,
    //     roles: user.roles,
    //     organization_roles: user.organization_roles,
    //   });
    // }

    // Platform users can access everything
    if (user.user_type === 'platform_user') return true;

    // For org users, check based on roles
    if (user.user_type === 'org_user') {
      const userRoles = user.roles || user.organization_roles || [];

      // org_admin can access all org-related menus
      if (userRoles.includes('org_admin')) return true;

      // Task 070-E: quản lý outlet chỉ thấy "Khách hàng" (server cũng chặn mọi route khác — allowlist).
      if (userRoles.includes('outlet_manager')) return key === 'customers' || key === 'voucher-usage';

      // Other roles have limited access
      if (userRoles.includes('event_manager')) {
        return [
          'org-dashboard',
          'events',
          'customers',
          'tags',
          'analytics',
          'payments',
          'giftcards',
          'vouchers',
          'promotions',
          'thirdparty',
          'unit-ledger',
          'cms',
          'fnbpos',
          'court',
          'tournaments',
          'settings'
        ].includes(key);
      }

      if (userRoles.includes('staff')) {
        return ['org-dashboard', 'events', 'cms', 'fnbpos', 'court', 'tournaments'].includes(key);
      }
    }

    // Default: only allow dashboard
    return key === 'org-dashboard';
  };

  const focusLoyalty = isLoyaltyFocus();
  const hiddenByFocus = (item: MenuItem): boolean => focusLoyalty && HIDDEN_IN_LOYALTY_FOCUS.has(String(item.key));

  const createMenuItem = (key: string, label: string, icon: React.ReactNode, path: string, _feature?: string): MenuItem | null => {
    if (!hasRoleAccess(key)) {
      return null;
    }

    const menuItem: MenuItem = {
      key,
      icon,
      label,
      onClick: () => onMenuClick(path),
    };

    return menuItem;
  };

  const modules = tenant?.organization?.settings?.modules;
  const on = (k: OrgModuleKey) => isModuleEnabled(modules, k);

  // Nhóm cấp 1 (ticket 121): chỉ hiện khi còn ít nhất một mục con; 1 mục con thì hiện thẳng mục đó.
  const group = (key: string, label: string, icon: React.ReactNode, children: (MenuItem | null)[]): MenuItem | null => {
    const kids = children.filter((c): c is MenuItem => c !== null && !hiddenByFocus(c));
    if (kids.length === 0) return null;
    if (kids.length === 1) return kids[0];
    return { key, icon, label, children: kids } as MenuItem;
  };

  const unitLedgerItem: MenuItem | null = on('unit_ledger') && hasRoleAccess('unit-ledger')
    ? {
        key: 'unit-ledger',
        icon: <AccountBookOutlined />,
        label: t('menu.organization.unitLedger'),
        children: [
          ['unit-ledger-pair-config', 'unitLedgerPairConfig', 'unit-ledger/pair-config'],
          ['unit-ledger-settlement-runs', 'unitLedgerSettlementRuns', 'unit-ledger/settlement-runs'],
          ['unit-ledger-manual-entry', 'unitLedgerManualEntry', 'unit-ledger/manual-entry'],
          ['unit-ledger-orphan-entries', 'unitLedgerOrphanEntries', 'unit-ledger/orphan-entries'],
          ['unit-ledger-owned-events', 'unitLedgerOwnedEvents', 'unit-ledger/owned-events'],
          ['unit-ledger-internal-balances', 'unitLedgerInternalBalances', 'unit-ledger/internal-balances'],
        ].map(([key, label, path]) => ({
          key,
          label: t(`menu.organization.${label}`),
          onClick: () => onMenuClick(getOrgUrl(path)),
        })),
      }
    : null;

  const menuItems: MenuItem[] = [
    createMenuItem('org-dashboard', t('menu.organization.dashboard'), <DashboardOutlined />, getOrgUrl('')),
    on('events')
      ? group('events-group', t('menu.organization.eventsAndTickets'), <CalendarOutlined />, [
          createMenuItem('events', t('menu.organization.eventsManagement'), <CalendarOutlined />, getOrgUrl('events'), 'events_enabled'),
          createMenuItem('payments', t('menu.organization.payments'), <WalletOutlined />, getOrgUrl('payments'), 'payments_enabled'),
        ])
      : null,
    createMenuItem('customers', t('menu.organization.customers'), <TeamOutlined />, getOrgUrl('customers')),
    // Ticket 086: dashboard loyalty riêng (chỉ quản trị tổ chức — báo cáo loyalty là org-admin-only ở bo-gateway).
    createMenuItem('loyalty-dashboard', t('menu.organization.loyaltyDashboard', { defaultValue: 'Dashboard Loyalty' }), <BarChartOutlined />, getOrgUrl('customers/loyalty-dashboard')),
    createMenuItem('crm-dashboard', t('menu.organization.crmDashboard', { defaultValue: 'Dashboard CRM' }), <TeamOutlined />, getOrgUrl('customers/crm-dashboard')),
    // Task 070-E: quản lý outlet xem báo cáo voucher (đã lọc theo outlet của họ, không CSV).
    isOutletManagerOnly
      ? createMenuItem('voucher-usage', t('menu.organization.voucherUsage', { defaultValue: 'Báo cáo voucher' }), <GiftOutlined />, getOrgUrl('vouchers/usage'))
      : null,
    group('promo-group', t('menu.organization.promotionsGroup'), <ThunderboltOutlined />, [
      createMenuItem('promotions', t('menu.organization.promotions'), <ThunderboltOutlined />, getOrgUrl('promotions')),
      createMenuItem('vouchers', t('menu.organization.vouchers'), <TagOutlined />, getOrgUrl('vouchers'), 'vouchers_enabled'),
      createMenuItem('giftcards', t('menu.organization.giftCards'), <GiftOutlined />, getOrgUrl('giftcards'), 'gift_cards_enabled'),
    ]),
    on('cms') && hasRoleAccess('cms') ? cmsMenuItems(getOrgUrl, t, onMenuClick) as MenuItem : null,
    on('fnbpos') && hasRoleAccess('fnbpos') && isOrgAdminUser ? fnbposMenuItems(getOrgUrl, t, onMenuClick) as MenuItem : null,
    on('court') && hasRoleAccess('court') ? courtMenuItems(getOrgUrl, t, onMenuClick) as MenuItem : null,
    on('tournaments') ? createMenuItem('tournaments', t('menu.organization.tournaments'), <TrophyOutlined />, getOrgUrl('tournaments')) : null,
    // Mục hiếm dùng gom vào "Khác" (không đổi URL).
    group('other-group', t('menu.organization.otherGroup'), <AppstoreOutlined />, [
      on('events') ? createMenuItem('tags', t('menu.organization.tags'), <TagsOutlined />, getOrgUrl('tags')) : null, // G9-19 tag sự kiện/suất chiếu
      on('events') && on('seating_plans') ? createMenuItem('seating-plans', t('menu.organization.seatingPlans'), <LayoutOutlined />, getOrgUrl('seating-plans')) : null,
      on('events') ? createMenuItem('order-groups', t('menu.organization.orderGroups'), <AppstoreOutlined />, getOrgUrl('order-groups')) : null,
      unitLedgerItem,
    ]),
    // 'users' KHÔNG có trong danh sách key cho phép của event_manager/staff (xem hasRoleAccess) — chỉ org_admin thấy,
    // khớp RequireOrgAdmin() ở backend (tạo/sửa/xoá được tài khoản đăng nhập).
    createMenuItem('users', t('menu.organization.users', { defaultValue: 'Người dùng' }), <UserOutlined />, getOrgUrl('users')),
    createMenuItem('settings', t('menu.organization.settings'), <SettingOutlined />, getOrgUrl('settings')),
  ].filter((item): item is MenuItem => item !== null && !hiddenByFocus(item));

  return { items: menuItems };
}
